import { useState } from 'react';
import { fitLiveApi } from './api';
import { getShellToken } from '../utils/shellToken';

export default function ImportExcelPanel({ onDone, setMsg, busy, setBusy }) {
  const [session, setSession] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    setMsg('');
    try {
      const result = await fitLiveApi.importValidate(file);
      setSession(result);
      setMsg(`${result.summary.ready} listas · ${result.summary.error} con error`);
    } catch (err) {
      setMsg(err.message || 'Error al validar Excel');
      setSession(null);
    } finally {
      setBusy(false);
      e.target.value = '';
    }
  };

  const patchField = async (excelRow, field, value) => {
    if (!session?.session_id) return;
    setBusy(true);
    try {
      const result = await fitLiveApi.importPatchRow(session.session_id, excelRow, { [field]: value });
      setSession(result);
    } catch (err) {
      setMsg(err.message || 'Error al corregir');
    } finally {
      setBusy(false);
    }
  };

  const revalidate = async () => {
    if (!session?.session_id) return;
    setBusy(true);
    try {
      const result = await fitLiveApi.importRevalidate(session.session_id);
      setSession(result);
      setMsg('Revalidado');
    } catch (err) {
      setMsg(err.message || 'Error al revalidar');
    } finally {
      setBusy(false);
    }
  };

  const publish = async () => {
    if (!session?.session_id) return;
    setBusy(true);
    setConfirmOpen(false);
    try {
      const result = await fitLiveApi.importPublish(session.session_id);
      setMsg(`Publicadas ${result.published_count} · omitidas ${result.skipped_count} · errores ${result.failed_count}`);
      setSession(null);
      await onDone?.();
    } catch (err) {
      setMsg(err.message || 'Error al publicar lote');
    } finally {
      setBusy(false);
    }
  };

  return (
    <section>
      <div className="fl-section-head">
        <div>
          <h2>Cargar clases desde Excel</h2>
          <p>Validar → preview → corregir → confirmar → publicar (nunca automático)</p>
        </div>
      </div>
      <div className="fl-actions">
        <button type="button" className="fl-btn" disabled={busy} onClick={() => fitLiveApi.downloadTemplate()}>
          Descargar plantilla Excel
        </button>
        <label className="fl-btn fl-btn-primary" style={{ cursor: busy ? 'not-allowed' : 'pointer' }}>
          Subir Excel
          <input type="file" accept=".xlsx,.xls" hidden disabled={busy} onChange={onFile} />
        </label>
        {session ? (
          <>
            <button type="button" className="fl-btn" disabled={busy} onClick={revalidate}>Revalidar</button>
            <a
              className="fl-btn"
              href={`/api/fit-live/import/${session.session_id}/errors.csv`}
              onClick={(e) => {
                e.preventDefault();
                const token = getShellToken();
                fetch(`/api/fit-live/import/${session.session_id}/errors.csv`, {
                  headers: token ? { Authorization: `Bearer ${token}` } : {},
                })
                  .then((r) => r.text())
                  .then((t) => {
                    const blob = new Blob([t], { type: 'text/csv' });
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = 'fit-live-import-errores.csv';
                    a.click();
                    URL.revokeObjectURL(url);
                  });
              }}
            >
              Descargar reporte errores
            </a>
          </>
        ) : null}
      </div>

      {session ? (
        <>
          <div className="fl-kpi-grid" style={{ marginTop: '1rem' }}>
            <div className="fl-kpi"><label>Listas ✓</label><strong>{session.summary.ready}</strong></div>
            <div className="fl-kpi"><label>Válidas</label><strong>{session.summary.valid}</strong></div>
            <div className="fl-kpi"><label>Warnings</label><strong>{session.summary.warning}</strong></div>
            <div className="fl-kpi"><label>Errores ⚠</label><strong>{session.summary.error}</strong></div>
          </div>

          <div className="fl-table-wrap" style={{ marginTop: '1rem' }}>
            <table className="fl-table">
              <thead>
                <tr>
                  <th>Fila</th>
                  <th>Estado</th>
                  <th>Clase</th>
                  <th>Disciplina</th>
                  <th>Nivel</th>
                  <th>Duración</th>
                  <th>Tokens</th>
                  <th>Detalle</th>
                </tr>
              </thead>
              <tbody>
                {session.rows.map((r) => (
                  <tr key={r.excel_row}>
                    <td>{r.excel_row}</td>
                    <td>{r.status === 'error' ? '⚠' : '✓'} {r.status}</td>
                    <td>
                      <input
                        className="fl-field"
                        defaultValue={r.data.title}
                        onBlur={(e) => {
                          if (e.target.value !== r.data.title) patchField(r.excel_row, 'title', e.target.value);
                        }}
                        style={{ width: '100%', minWidth: 120 }}
                      />
                    </td>
                    <td>
                      <input
                        className="fl-field"
                        defaultValue={r.data.discipline}
                        onBlur={(e) => {
                          if (e.target.value !== r.data.discipline) patchField(r.excel_row, 'discipline', e.target.value);
                        }}
                        style={{ width: 100 }}
                      />
                    </td>
                    <td>
                      <input
                        className="fl-field"
                        defaultValue={r.data.level}
                        onBlur={(e) => {
                          if (e.target.value !== r.data.level) patchField(r.excel_row, 'level', e.target.value);
                        }}
                        style={{ width: 100 }}
                      />
                    </td>
                    <td>
                      <input
                        className="fl-field"
                        type="number"
                        defaultValue={r.data.duration_min}
                        onBlur={(e) => {
                          const v = Number(e.target.value);
                          if (v !== r.data.duration_min) patchField(r.excel_row, 'duration_min', v);
                        }}
                        style={{ width: 72 }}
                      />
                    </td>
                    <td>
                      <input
                        className="fl-field"
                        type="number"
                        defaultValue={r.data.price_tokens}
                        onBlur={(e) => {
                          const v = Number(e.target.value);
                          if (v !== r.data.price_tokens) patchField(r.excel_row, 'price_tokens', v);
                        }}
                        style={{ width: 72 }}
                      />
                    </td>
                    <td style={{ fontSize: '0.75rem', color: 'var(--fl-muted)', maxWidth: 240 }}>
                      {[...r.errors, ...r.warnings].join(' · ') || '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="fl-actions" style={{ marginTop: '1rem' }}>
            <button
              type="button"
              className="fl-btn fl-btn-primary"
              disabled={busy || session.summary.ready < 1}
              onClick={() => setConfirmOpen(true)}
            >
              Publicar clases (
              {session.summary.ready}
              )
            </button>
          </div>

          {confirmOpen ? (
            <div className="fl-table-wrap" style={{ marginTop: '1rem', padding: '1rem' }}>
              <p>
                {session.summary.ready}
                {' '}
                clases quedarán disponibles en tu catálogo y podrán ser reservadas según precio y disponibilidad.
              </p>
              <div className="fl-actions">
                <button type="button" className="fl-btn" onClick={() => setConfirmOpen(false)}>Cancelar</button>
                <button type="button" className="fl-btn fl-btn-primary" disabled={busy} onClick={publish}>
                  Confirmar publicación
                </button>
              </div>
            </div>
          ) : null}
        </>
      ) : null}
    </section>
  );
}
